import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {ActivitiesGroup} from '../../../../../../../../../backend/interfaces/activities-group';
import {ActivitiesSector} from '../../../../../../../../../backend/interfaces/activities-sector';
import {ActivityType} from '../../../../../../../../../backend/interfaces/activity-type';
import {FlatSelectDropdown} from '../../../../../../../../../components/flat-select-dropdown/flat-select-dropdown';
import {translations} from '../../../../../../../../../i18n/translations';
import {useFetchActivitiesGroup} from '../../../shared/use-fetch-activities-groups';
import {useFetchActivitiesGroupTypes} from '../../../shared/use-fetch-activities-group-types';

export type PlannerActivityChooseProps = {
  activitiesSector: ActivitiesSector;
  activitiesGroup?: ActivitiesGroup;
  activitiesType?: ActivityType;
  onActivitiesGroupChange: (activitiesGroup: ActivitiesGroup) => void;
  onActivitiesTypeChange: (activityType?: ActivityType) => void;
};

export function PlannerActivityChoose(props: PlannerActivityChooseProps) {
  return (
    <>
      <ActivitiesGroupSelectDropdown
        activitiesSector={props.activitiesSector}
        value={props.activitiesGroup}
        onChange={props.onActivitiesGroupChange}
      />

      {props.activitiesGroup && (
        <ActivitiesGroupTypeSelectDropown
          activitiesSector={props.activitiesSector}
          activitiesGroup={props.activitiesGroup}
          value={props.activitiesType}
          onChange={props.onActivitiesTypeChange}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------

function ActivitiesGroupSelectDropdown(props: {
  activitiesSector: ActivitiesSector;
  value?: ActivitiesGroup;
  onChange: (activitiesGroup: ActivitiesGroup) => void;
}) {
  const {t} = useTranslation();

  // --- Fetch activities group ---
  const {items, isLoading} = useFetchActivitiesGroup(props.activitiesSector.id!);
  const values = items?.map((item) => ({key: item.id, label: item.name}));

  return (
    <FlatSelectDropdown
      label={t(translations.planner.modal.create.activity.ativities_group)}
      placeholder={t(translations.planner.modal.create.activity.ativities_group_placeholder)}
      required
      onChange={(option) => {
        const selectedActivitiesGroup = items?.find((item) => item.id === option.key);
        if (selectedActivitiesGroup) props.onChange(selectedActivitiesGroup);
      }}
      options={values ?? []}
      loading={isLoading}
      value={
        props.value && {
          key: props.value.id,
          label: props.value?.name,
        }
      }
    />
  );
}

function ActivitiesGroupTypeSelectDropown({
  onChange,
  ...props
}: {
  activitiesSector: ActivitiesSector;
  activitiesGroup: ActivitiesGroup;
  value?: ActivityType;
  onChange: (activitiesGroupType?: ActivityType) => void;
}) {
  const {t} = useTranslation();

  // --- Fetch activities group types ---
  const {items, isLoading} = useFetchActivitiesGroupTypes(
    props.activitiesSector.id!,
    props.activitiesGroup.id
  );

  const values = items?.map((item) => ({key: item.id!, label: item.name}));

  // --- Check if the activities group has types ---
  useEffect(() => {
    if (!isLoading && items?.length === 0) {
      // Activities group has no types. Signal it onChange.
      onChange(undefined);
    }
  }, [items, isLoading, onChange]);

  return (
    <FlatSelectDropdown
      label={t(translations.planner.modal.create.activity.activities_type)}
      placeholder={t(translations.planner.modal.create.activity.activities_type_placeholder)}
      required
      onChange={(option) => {
        const selectedActivitiesGroupType = items?.find((item) => item.id === option.key);
        if (selectedActivitiesGroupType) onChange(selectedActivitiesGroupType);
      }}
      options={values ?? []}
      loading={isLoading}
      value={
        props.value && {
          key: props.value.id!,
          label: props.value?.name,
        }
      }
      sx={{marginTop: '16px'}}
    />
  );
}
